import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { TransactionUpload } from '~/types/fundrequests';
import { MdClose, MdDownload, MdNavigateBefore, MdNavigateNext, MdZoomIn, MdZoomOut, MdCenterFocusWeak, MdRotateLeft, MdRotateRight } from 'react-icons/md';
import { isPdfFile, isImageFile } from '~/components/FundRequests/utils/fileType';

// Set up the worker for react-pdf - use stable CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentPreviewProps {
    isOpen: boolean;
    onClose: () => void;
    uploads: TransactionUpload[];
    slideIndex: number | null;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
    isOpen,
    onClose,
    uploads,
    slideIndex,
}) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    
    // Zoom and pan state for images
    const [zoomLevel, setZoomLevel] = useState<number>(1);
    const [panX, setPanX] = useState<number>(0);
    const [panY, setPanY] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    // PDF orientation state. Some uploads (e.g. phone photos converted to PDF by
    // Acrobat) carry a /Rotate flag that pdf.js applies but the original viewer
    // ignores, so the page renders rotated. baseRotation is the page's own
    // rotation; userRotation is the correction the admin applies.
    const [baseRotation, setBaseRotation] = useState<number>(0);
    const [userRotation, setUserRotation] = useState<number>(0);

    // Early return if not open or no uploads
    if (!isOpen || !uploads || uploads.length === 0 || slideIndex === null) {
        return null;
    }

    // Ensure slideIndex is within bounds
    const safeSlideIndex = Math.max(0, Math.min(slideIndex, uploads.length - 1));
    const currentUpload = uploads[safeSlideIndex];
    
    if (!currentUpload) {
        return null;
    }

    const isPdf = isPdfFile(currentUpload?.filename);
    const isImage = isImageFile(currentUpload?.filename);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
        setLoading(false);
    }

    function onDocumentLoadError(error: Error) {
        console.error('Error loading PDF:', error);
        setLoading(false);
    }

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = currentUpload.image_url;
        link.download = currentUpload.filename || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const goToPrevPage = () => {
        setPageNumber(prev => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setPageNumber(prev => Math.min(numPages, prev + 1));
    };

    // Zoom functions
    const zoomIn = () => {
        setZoomLevel(prev => Math.min(prev * 1.5, 5)); // Max zoom 5x
    };

    const zoomOut = () => {
        setZoomLevel(prev => Math.max(prev / 1.5, 0.5)); // Min zoom 0.5x
    };

    const resetZoom = () => {
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setUserRotation(0);
    };

    const rotateLeft = () => {
        setUserRotation(prev => (prev + 270) % 360);
    };

    const rotateRight = () => {
        setUserRotation(prev => (prev + 90) % 360);
    };

    // Capture the page's intrinsic rotation so admin corrections are applied
    // relative to however pdf.js renders it by default.
    function onPageLoadSuccess(page: { rotate?: number }) {
        setBaseRotation(page?.rotate ?? 0);
    }

    // Mouse handlers for pan functionality
    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoomLevel > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging && zoomLevel > 1) {
            setPanX(e.clientX - dragStart.x);
            setPanY(e.clientY - dragStart.y);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Wheel zoom handler
    const handleWheel = (e: React.WheelEvent) => {
        if (isImage || isPdf) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            setZoomLevel(prev => Math.max(0.5, Math.min(5, prev * delta)));
        }
    };

    // Reset page number, zoom, and rotation when document changes
    useEffect(() => {
        setPageNumber(1);
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setUserRotation(0);
        // Always start with loading true when document changes
        setLoading(true);
    }, [currentUpload?.image_url]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isOpen) return;
            
            switch (event.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    if (isPdf) {
                        event.preventDefault();
                        goToPrevPage();
                    }
                    break;
                case 'ArrowRight':
                    if (isPdf) {
                        event.preventDefault();
                        goToNextPage();
                    }
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isPdf, numPages, pageNumber]);

    return (
        <div className="h-full flex flex-col bg-white border border-stroke rounded-lg shadow-lg overflow-hidden">
            {/* Header with controls */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-800 truncate">
                            {currentUpload.filename || 'Document'}
                        </h3>
                        {isPdf && numPages > 0 && (
                            <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
                                Page {pageNumber} of {numPages}
                            </span>
                        )}
                        {isImage && zoomLevel > 1 && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                                Drag to pan • Scroll to zoom
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {isPdf && numPages > 0 && (
                            <>
                                <button
                                    onClick={goToPrevPage}
                                    disabled={pageNumber <= 1}
                                    className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Previous page"
                                >
                                    <MdNavigateBefore size={20} />
                                </button>
                                <button
                                    onClick={goToNextPage}
                                    disabled={pageNumber >= numPages}
                                    className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Next page"
                                >
                                    <MdNavigateNext size={20} />
                                </button>
                                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                            </>
                        )}

                        {(isImage || isPdf) && (
                            <>
                                <div className="flex items-center gap-1">
                                    {isPdf && (
                                        <>
                                            <button
                                                onClick={rotateLeft}
                                                className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                                                title="Rotate left"
                                            >
                                                <MdRotateLeft size={16} />
                                            </button>
                                            <button
                                                onClick={rotateRight}
                                                className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                                                title="Rotate right"
                                            >
                                                <MdRotateRight size={16} />
                                            </button>
                                            <div className="w-px h-4 bg-gray-300 mx-1"></div>
                                        </>
                                    )}
                                    <button
                                        onClick={zoomOut}
                                        disabled={zoomLevel <= 0.5}
                                        className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                        title="Zoom out"
                                    >
                                        <MdZoomOut size={16} />
                                    </button>
                                    <span className="text-xs text-gray-500 min-w-[35px] text-center">
                                        {Math.round(zoomLevel * 100)}%
                                    </span>
                                    <button
                                        onClick={zoomIn}
                                        disabled={zoomLevel >= 5}
                                        className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                        title="Zoom in"
                                    >
                                        <MdZoomIn size={16} />
                                    </button>
                                    <button
                                        onClick={resetZoom}
                                        disabled={zoomLevel === 1 && panX === 0 && panY === 0 && userRotation === 0}
                                        className="p-1 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                        title="Reset view"
                                    >
                                        <MdCenterFocusWeak size={16} />
                                    </button>
                                </div>
                                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                            </>
                        )}
                        
                        <button
                            onClick={handleDownload}
                            className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                            title="Download"
                        >
                            <MdDownload size={20} />
                        </button>
                        
                        <button
                            onClick={onClose}
                            className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                            title="Close preview"
                        >
                            <MdClose size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Document Content */}
            <div className="flex-1 overflow-auto bg-gray-50 flex flex-col items-center">
                {loading && (
                    <div className="text-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading {isPdf ? 'PDF' : 'image'}...</p>
                    </div>
                )}

                {isPdf ? (
                    <div className="w-full flex-1 flex flex-col items-center pt-2">
                        <Document
                            file={currentUpload.image_url}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={null}
                            error={
                                <div className="text-center p-8">
                                    <p className="text-red-500 mb-2 text-lg">Failed to load PDF</p>
                                    <p className="text-gray-600 text-sm mb-4">Please check if the file is a valid PDF document.</p>
                                    <button
                                        onClick={handleDownload}
                                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                    >
                                        Try downloading instead
                                    </button>
                                </div>
                            }
                        >
                            {!loading && (
                                <Page
                                    key={`page_${pageNumber}`}
                                    pageNumber={pageNumber}
                                    onLoadSuccess={onPageLoadSuccess}
                                    rotate={(baseRotation + userRotation) % 360}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                    width={Math.min(1300, window.innerWidth - 540) * zoomLevel} // Account for drawer width and padding
                                    className="shadow-lg border border-gray-200"
                                />
                            )}
                        </Document>
                    </div>
                ) : isImage ? (
                    <div 
                        className="w-full flex-1 flex items-center justify-center pt-2 overflow-hidden"
                        onWheel={handleWheel}
                    >
                        <div 
                            className="relative select-none"
                            style={{
                                cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <img
                                src={currentUpload.image_url}
                                alt={currentUpload.filename}
                                className="shadow-lg border border-gray-200 rounded transition-transform duration-200"
                                onLoad={() => setLoading(false)}
                                onError={() => setLoading(false)}
                                style={{ 
                                    maxHeight: zoomLevel === 1 ? 'calc(100vh - 80px)' : 'none',
                                    maxWidth: zoomLevel === 1 ? 'calc(100vw - 520px)' : 'none',
                                    transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                                    transformOrigin: 'center center'
                                }}
                                draggable={false}
                            />
                        </div>
                    </div>
                ) : !loading ? (
                    <div className="text-center p-8">
                        <p className="text-gray-600 mb-4">Preview not available for this file type.</p>
                        <button
                            onClick={handleDownload}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        >
                            Download File
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default DocumentPreview;